import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HistoricalTestResult, LinkQuality } from '../types';
import { DownloadIcon, UploadIcon, PingIcon, ChartIcon, MonitorIcon, StarIcon } from 'shared-icons';
import { formatSpeed, calculateLinkQuality } from 'shared-grading';

interface HistoryPanelProps {
  history: HistoricalTestResult[];
  onClear: () => void;
  onOpenChart: () => void;
}

const getGradeColorClasses = (grade: LinkQuality['grade']): string => {
    switch (grade) {
        case 'A': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
        case 'B': return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300';
        case 'C': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
        case 'D': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
        case 'F': default: return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    }
}

// A memoized component for list items to prevent re-renders of items that are already visible
const HistoryItem = React.memo(({ result }: { result: HistoricalTestResult }) => {
    const formattedDownload = formatSpeed(result.download);
    const formattedUpload = formatSpeed(result.upload);
    const quality = calculateLinkQuality(result);

    return (
        <div className="bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/30 dark:border-slate-700/50 flex flex-col gap-3">
            <div className="flex justify-between items-start">
                <div>
                    <p className="font-semibold text-slate-800 dark:text-slate-200">{result.serverName}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(result.date).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded-full ${getGradeColorClasses(quality.grade)}`}>
                        Grade {quality.grade}
                    </span>
                    {result.connectionMode && <span className="bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded-full capitalize">{result.connectionMode}</span>}
                    {result.testProtocol && <span className="bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded-full uppercase">{result.testProtocol}</span>}
                </div>
            </div>
            <div className="flex items-center justify-between gap-4 text-sm font-semibold">
                <div className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400" title="Download">
                    <DownloadIcon width={16} height={16} />
                    <span>{formattedDownload.value} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">{formattedDownload.unit}</span></span>
                </div>
                <div className="flex items-center gap-1.5 text-purple-600 dark:text-purple-400" title="Upload">
                    <UploadIcon width={16} height={16} />
                    <span>{formattedUpload.value} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">{formattedUpload.unit}</span></span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300" title="Ping">
                    <PingIcon width={16} height={16} />
                    <span>{result.ping} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">ms</span></span>
                </div>
            </div>
             <div className="border-t border-gray-200 dark:border-slate-700/50 pt-2 mt-2 text-xs text-slate-500 dark:text-slate-400 flex justify-between items-center">
                <span>ISP: {result.isp}</span>
                {result.deviceInfo && (
                    <span className="flex items-center gap-1">
                        <MonitorIcon width={12} height={12} /> {result.deviceInfo}
                    </span>
                )}
            </div>
             {result.feedbackRating && (
                <div className="border-t border-gray-200 dark:border-slate-700/50 pt-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold">Your Feedback:</span>
                        <div className="flex">
                            {[...Array(5)].map((_, i) => (
                                <StarIcon key={i} width={14} height={14} className="text-yellow-400" filled={i < result.feedbackRating!} />
                            ))}
                        </div>
                    </div>
                    {result.feedbackComment && <p className="mt-1 italic">"{result.feedbackComment}"</p>}
                </div>
            )}
        </div>
    )
});

const HistoryPanel: React.FC<HistoryPanelProps> = ({ history, onClear, onOpenChart }) => {
  // Robustness: Ensure history is always an array to prevent crashes from invalid props.
  const historyArray = Array.isArray(history) ? history : [];
  if (!Array.isArray(history)) {
    console.error("HistoryPanel received a non-array prop. Fallback to empty array.", { received: history });
  }

  const [visibleCount, setVisibleCount] = useState(10);
  const observer = useRef<IntersectionObserver | null>(null);

  const loaderRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && historyArray.length > visibleCount) {
        setVisibleCount(prev => prev + 10);
      }
    });

    if (node) observer.current.observe(node);
  }, [historyArray.length, visibleCount]);

  useEffect(() => {
    setVisibleCount(10);
  }, [historyArray]);

  const visibleHistory = historyArray.slice(0, visibleCount);
  const hasMore = visibleCount < historyArray.length;

  return (
    <div className="w-full max-w-4xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">Test History</h2>
        <div className="flex items-center gap-2">
          {historyArray.length > 1 && (
            <button
              onClick={onOpenChart}
              className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-full text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600/50 transition flex items-center gap-2"
            >
              <ChartIcon width={16} height={16} />
              <span>Chart</span>
            </button>
          )}
          {historyArray.length > 0 && (
            <button
              onClick={onClear}
              className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-full text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600/50 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {historyArray.length === 0 ? (
        <p className="text-center text-slate-500 dark:text-slate-400 py-8">Your test history will appear here.</p>
      ) : (
        <div className="space-y-3">
          {visibleHistory.map((result) => (
            <HistoryItem key={result.id} result={result} />
          ))}
          {hasMore && (
            <div ref={loaderRef} className="flex justify-center items-center py-4">
               <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400 dark:border-slate-500"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryPanel;